import { createBrowserRouter } from "react-router-dom";
import React, { Suspense } from "react";
import { Typography, CircularProgress, Box } from "@mui/material";
import Home from "../pages/Home";
import LoginForm from "../pages/Login";
import Layout from "./Layout";
import ProtectedRoutes from "./ProtectedRoutes";
import SignUp from "../pages/SignUp";
import BlogWrapper from "./BlogWrapper";
import Annonces from "../pages/Annonces";

// Lazy loading des pages moins critiques pour réduire le bundle initial
const Annonce = React.lazy(() => import("../pages/annonce/Annonce"));
const Dashboard = React.lazy(() => import("../pages/Dashboard"));
const CalendrierDispos = React.lazy(() => import("../pages/calendrier/CalendrierDispos"));
const ManageRdv = React.lazy(() => import("../pages/rdv/ManageRdv"));
const Retour = React.lazy(() => import("../pages/retour/Retour"));
const Page = React.lazy(() => import("../pages/modification/Edition"));
const MonCompte = React.lazy(() => import("../pages/MonCompte"));
const Blog = React.lazy(() => import("../pages/Blog"));
const MentionsLegales = React.lazy(() => import("../pages/MentionsLegales"));
const PolitiqueConfidentialite = React.lazy(() => import("../pages/PolitiqueConfidentialite"));
const CGV = React.lazy(() => import("../pages/CGV"));
const Assistance = React.lazy(() => import("../pages/Assistance"));
const Article = React.lazy(() => import("../pages/blog/Article"));
const Coaching = React.lazy(() => import("../pages/Coaching"));
const Success = React.lazy(() => import("../pages/Success"));
const Cancel = React.lazy(() => import("../pages/Cancel"));
const Comparer = React.lazy(() => import("../pages/Comparer"));
const Favoris = React.lazy(() => import("../pages/Favoris"));

// Composant de chargement pour Suspense
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

// Helper pour envelopper les composants lazy avec Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <Typography>Une erreur s'est produite</Typography>,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: "connexion",
        element: <LoginForm />,
      },
      {
        path: "creer-compte",
        element: <SignUp />,
      },
      {
        path: "mentions-legales",
        element: withSuspense(MentionsLegales),
      },
      {
        path: "politique-confidentialite",
        element: withSuspense(PolitiqueConfidentialite),
      },
      {
        path: "cgv",
        element: withSuspense(CGV),
      },
      {
        path: "assistance",
        element: withSuspense(Assistance),
      },
      { path: "coaching", element: withSuspense(Coaching) },
      {
        path: "success",
        element: withSuspense(Success),
      },
      {
        path: "cancel",
        element: withSuspense(Cancel),
      },
      // {
      //   path: "trouver-une-annonce",
      //   element: <TrouverAnnonce />,
      // },
      {
        path: "blog",
        element: <BlogWrapper />,
        children: [
          {
            index: true,
            element: withSuspense(Blog),
          },
          {
            path: ":articleSlug",
            element: withSuspense(Article),
          },
        ],
      },

      {
        path: "annonce",
        children: [
          {
            path: ":annonceId",
            element: withSuspense(Annonce),
          },
        ],
      },
      { path: "annonces", element: <Annonces /> },
      { path: "comparer", element: withSuspense(Comparer) },
      { path: "favoris", element: withSuspense(Favoris) },
      {
        path: "calendrier/:vendeurId",
        element: withSuspense(CalendrierDispos),
      },
      {
        path: "rdv/:rdvId",
        element: withSuspense(ManageRdv),
      },
      {
        path: "retour/:reponseId",
        element: withSuspense(Retour),
      },
      {
        element: <ProtectedRoutes />,
        children: [
          {
            path: "mon-compte",
            element: withSuspense(MonCompte),
          },
          {
            path: "tableau-de-bord",
            element: withSuspense(Dashboard),
          },
          {
            path: "/modification",
            children: [
              {
                path: ":annonceId",
                element: withSuspense(Page),
              },
            ],
          },
        ],
      },
    ],
  },
]);
